import {Component, inject, OnDestroy, OnInit, Renderer2, ViewChildren, QueryList} from '@angular/core';
import {Subscription} from 'rxjs';
import {DraftStatus, DraftType, UserSettings} from '@xmedical/core';
import {DraftsService, PatientDraft} from '../../../services/drafts.service';
import {DraftInteractionBehaviour} from '../draft-interaction-behaviour';
import {Permissions} from '@xmedical/core';
import {CoreService} from '../../../services/core.service';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';

@Component({
    selector: 'ui-draft-overlay-draggable', templateUrl: './draft-overlay-draggable.component.html', styleUrls: ['./draft-overlay-draggable.component.scss'],
    standalone: false
})
export class DraftOverlayDraggableComponent implements OnInit, OnDestroy {

  drafts: PatientDraft[] = [];
  isMobile: boolean;
  hasOpenDrafts = false;
  @ViewChildren('draftComponent') allDraftComponents: QueryList<DraftInteractionBehaviour>;
  readonly Permissions = Permissions;
  private draftEventSubscription: Subscription;
  private breakpointObserverSubscription: Subscription;
  private draftService = inject(DraftsService);
  private renderer = inject(Renderer2);
  private breakpointObserver = inject(BreakpointObserver);
  private coreService = inject(CoreService);

  get userSettings(): UserSettings {
    return this.coreService.settings();
  }

  get draftStatus(): typeof DraftStatus {
    return DraftStatus;
  }

  ngOnInit(): void {
    this.draftEventSubscription = this.draftService.events$.subscribe(() => {
      this.drafts = this.draftService.getDrafts();
      this.updateOpenDrafts();
    });
    this.breakpointObserverSubscription = this.breakpointObserver.observe([Breakpoints.Handset]).subscribe(result => {
      this.isMobile = result.matches;
      this.updateOpenDrafts();
    });
  }

  ngOnDestroy(): void {
    this.draftEventSubscription?.unsubscribe();
    this.breakpointObserverSubscription?.unsubscribe();
  }

  minimizeAllDrafts() {
    this.draftService.minimizeAllDrafts();
    this.updateOpenDrafts();
  }

  minimizeDraft(draftId: number, draftType: DraftType) {
    this.draftService.minimizeDraft(draftId, draftType);
    this.updateOpenDrafts();
  }

  maximizeDraft(draftId: number, draftType: DraftType) {
    this.draftService.maximizeDraft(draftId, draftType);
    this.updateOpenDrafts();
  }

  restoreDraft(draftId: number, draftType: DraftType) {
    this.draftService.restoreDraft(draftId, draftType);
    this.updateOpenDrafts();
  }

  closeDraft(draftId: number, draftType: DraftType) {
    this.draftService.closeDraft(draftId, draftType);
    this.updateOpenDrafts();
  }

  getSaveButtonLabel(draftType: DraftType): string {
    switch (draftType) {
      case 'comment': return 'Save Comment';
      case 'email': return 'Send Email';
      case 'message': return 'Send Message';
      case 'report': return 'Save Report';
      case 'script': return 'Save Script';
      default: return 'Save';
    }
  }

  getSaveConfirmationMessage(draftType: DraftType): string {
    switch (draftType) {
      case 'comment': return 'Are you sure you want to save this comment?';
      case 'email': return 'Are you sure you want to send this email?';
      case 'message': return 'Are you sure you want to send this message?';
      case 'report': return 'Are you sure you want to save this report?';
      case 'script': return 'Are you sure you want to save this script?';
      default: return 'Are you sure you want to save?';
    }
  }

  getSavePermission(draftType: DraftType): string {
    switch (draftType) {
      case 'comment': return Permissions.ClinicalCommentsWrite;
      case 'email': return Permissions.ClinicalEmailsWrite;
      case 'message': return Permissions.ClinicalMessagesWrite;
      case 'report': return Permissions.ClinicalReportsWrite;
      case 'script': return Permissions.ClinicalScriptsWrite;
      default: return '';
    }
  }

  getActiveComponent(draftType: DraftType, draftId: number): DraftInteractionBehaviour | undefined {
    return this.allDraftComponents?.find(c =>
      c.draft?.draftId === draftId && c.draft?.draftType === draftType
    );
  }

  isSaveButtonDisabled(draftType: DraftType, draftId: number): boolean {
    const component = this.getActiveComponent(draftType, draftId);
    return !component || !component.saveButtonEnabled;
  }

  saveCurrentDraft(draftType: DraftType, draftId: number): void {
    const component = this.getActiveComponent(draftType, draftId);
    if (component && component.save) {
      component.save();
    }
  }

  private updateOpenDrafts() {
    const openDrafts = this.drafts.filter(d => (d.draftStatus === DraftStatus.OPEN || d.draftStatus === DraftStatus.MAXIMIZED));
    this.hasOpenDrafts = openDrafts.length > 0;
    if (openDrafts.length > 0 && this.isMobile) {
      this.renderer.addClass(document.body, 'lock-scroll');
    } else {
      this.renderer.removeClass(document.body, 'lock-scroll');
    }
  }
}
